import type { ReactNode } from 'react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

export function SettingsCard({ title, description, action, children, testId }: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section className="rounded-2xl border border-line bg-surface shadow-card" data-testid={testId}>
      <div className="flex items-start justify-between gap-4 px-4 pt-4 sm:px-[22px] sm:pt-5">
        <div className="flex flex-col gap-1">
          <h2 className="font-display text-[17px] font-semibold tracking-tight text-ink sm:text-[18px]">{title}</h2>
          {description && <p className="text-body text-soft">{description}</p>}
        </div>
        {action}
      </div>
      <div className="flex flex-col gap-[18px] px-4 pb-4 pt-4 sm:px-[22px] sm:pb-[22px]">{children}</div>
    </section>
  );
}

export function ToggleRow({ id, label, hint, checked, onChange, disabled, children, first, control }: {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  checked?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  children?: ReactNode;
  first?: boolean;
  control?: ReactNode;
}) {
  return (
    <div className={cn('py-4', !first && 'border-t border-line')} data-testid={`row-${id}`}>
      <div className="flex items-start gap-4">
        <div className="flex flex-1 flex-col gap-1">
          <label htmlFor={`switch-${id}`} className="text-[15px] font-semibold text-ink">{label}</label>
          {hint && <span className="text-body-sm text-soft">{hint}</span>}
        </div>
        {control ?? (
          <Switch id={`switch-${id}`} checked={!!checked} onCheckedChange={onChange} disabled={disabled} aria-label={typeof label === 'string' ? label : undefined} />
        )}
      </div>
      {children && <div className="mt-3.5">{children}</div>}
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label, grow }: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (value: T) => void;
  label: string;
  grow?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cn('gap-0.5 rounded-lg bg-surface-2 p-[3px]', grow ? 'flex' : 'inline-flex')}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex h-9 min-h-[36px] items-center justify-center gap-1.5 whitespace-nowrap rounded-sm px-3.5 text-[13px]',
              grow && 'flex-1',
              active ? 'bg-surface font-semibold text-ink shadow-sm' : 'font-medium text-soft hover:text-ink',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function InfoNote({ children }: { children: ReactNode }) {
  return <div className="rounded-lg bg-brand-50 px-3.5 py-3 text-body-sm text-brand-700">{children}</div>;
}
